import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Icon } from "@/components/Icon";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IJobRequestFilter } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents";
import { useToggle } from "@/hooks/Toggle";

import { useJobRequestsFilters } from "./JobRequestsFiltersContext";
import { SaveSearchDialogModal } from "./SaveSearchModal";
import { useSavedSearchesActions } from "./useSavedSearchesActions";

import "./_filter-tab-buttons.scss";

interface ISearchTabButtonsProps {
  active?: boolean;
  savedSearch: IJobRequestFilter;
  onSearchSaved?: (filter: IJobRequestFilter) => void;
  onSearchDeleted?: (id: number) => void;
}

export const FilterTabButtons: FC<ISearchTabButtonsProps> = ({
  savedSearch,
  active,
  onSearchSaved,
  onSearchDeleted,
}) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const [settingsOpen, toggleSettingsOpen] = useToggle(false);

  const baseClass = "filter-tab-buttons";

  const { filters, savedSearchChanged } = useJobRequestsFilters();
  const { updateSearch } = useSavedSearchesActions();

  const handleSave = useCallback(
    async (e: React.MouseEvent<HTMLSpanElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const newSearch = await updateSearch(filters);
      if (newSearch) {
        onSearchSaved?.(newSearch);
      }
    },
    [filters, updateSearch, onSearchSaved],
  );

  const handleSettingsClick = useCallback(
    async (e: React.MouseEvent<HTMLSpanElement>) => {
      e.preventDefault();
      e.stopPropagation();

      toggleSettingsOpen();
    },
    [toggleSettingsOpen],
  );

  const handleModalClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const showSave = active && savedSearchChanged;
  const showSettings = !showSave;

  return (
    <Flex className={baseClass} direction="row" justify="flex-end">
      {showSave && !isSmallScreen && (
        <span className={`${baseClass}__save-button`} onClick={handleSave}>
          {t("JOB_REQUESTS.LIST.FILTERS.BUTTON_SAVE")}
        </span>
      )}

      {showSettings && !isSmallScreen && (
        <span onClick={handleSettingsClick}>
          <Icon className={`${baseClass}__settings-button`} icon="settings_fill" size="md" color="grey4" />
        </span>
      )}

      <div onClick={handleModalClick}>
        <SaveSearchDialogModal
          open={settingsOpen}
          savedSearch={savedSearch}
          onClose={toggleSettingsOpen}
          onSearchSaved={onSearchSaved}
          onSearchDeleted={onSearchDeleted}
          size="xxs"
        />
      </div>
    </Flex>
  );
};
